'use client';

import React, { useState, useEffect } from 'react';
import Breadcrumb from '../../components/Breadcrumb';
import RecordAgentPanel from '../../components/crm/RecordAgentPanel';
import Tabs from '../../components/ui/Tabs';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { CustomerFinancePanel } from '../../components/crm/billing/CustomerFinancePanel';
import {
  Building2, Mail, Phone, Globe, MapPin, Users, TrendingUp,
  Edit2, Save, X, ArrowLeft, AlertCircle, Loader,
  Calendar, Activity, ExternalLink, Hash, Crosshair, Briefcase,
  Layers, ChevronRight, ArrowUpRight, ShieldCheck,
  Clock, Info, Zap, Link, User, Plus, Receipt, Download, Eye, FileText, Share2, UserCog, History, ListChecks, Target
} from 'lucide-react';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import { formatCurrency, convertToBase } from '../../utils/currency';
import { formatDate } from '../../utils/formatDate';
import { downloadInvoicePdf, previewInvoicePdf } from '../../utils/invoicePdf';
import { useCompanyCurrency, useCurrencyRates } from '../../hooks/useCompanySettings';
import RelatedFiles from '../../components/crm/RelatedFiles';
import FormActivityPanel from '../../components/crm/FormActivityPanel';
import ShareModal from '../../components/crm/ShareModal';
import TransferOwnershipModal from '../../components/crm/TransferOwnershipModal';
import UnifiedTimeline from '../../components/crm/UnifiedTimeline';
import AccountHierarchyTree from '../../components/crm/AccountHierarchyTree';
import { PageLoader } from '../../components/crm/ui';

const TABS = [
  { id: 'overview', label: 'Details', icon: Info },
  { id: 'contacts', label: 'Company Contacts', icon: Users },
  { id: 'opportunities', label: 'Revenue Pipeline', icon: TrendingUp },
  { id: 'tasks', label: 'Tasks', icon: ListChecks },
  { id: 'invoices', label: 'Invoices', icon: Receipt },
  { id: 'finance', label: 'Finance', icon: Receipt },
  { id: 'files', label: 'Files', icon: FileText },
  { id: 'formActivity', label: 'Form Activity', icon: FileText },
  { id: 'history', label: 'Timeline', icon: History },
  { id: 'hierarchy', label: 'Hierarchy', icon: Layers },
];

export default function AccountDetailPage() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const baseCurrency = useCompanyCurrency();
  const ratesCtx = useCurrencyRates();

  const [account, setAccount] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [leads, setLeads] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [activities, setActivities] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [health, setHealth] = useState(null);
  const [pdfActionId, setPdfActionId] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [hierarchyMode, setHierarchyMode] = useState('relationships');
  const [formData, setFormData] = useState(null);

  useEffect(() => { fetchAccountData(); fetchHealth(); fetchSubsidiaries(); }, [accountId]);

  const fetchHealth = async () => {
    try {
      setHealthLoading(true);
      const res = await api.get(`/crm/accounts/${accountId}/health`);
      setHealth(res.data.health || null);
    } catch (err) {
      setHealth(null);
    } finally {
      setHealthLoading(false);
    }
  };

  const fetchSubsidiaries = async () => {
    try {
      const res = await api.get(`/crm/accounts/${accountId}/subsidiaries`);
      setSubsidiaries(res.data.subsidiaries || []);
    } catch (err) {
      setSubsidiaries([]);
    }
  };

  const fetchAccountData = async () => {
    try {
      setLoading(true);
      const [accountRes, invoicesRes] = await Promise.all([
        api.get(`/crm/accounts/${accountId}`),
        api.get('/crm/invoices', { params: { accountId } }),
      ]);
      if (accountRes.data.success) {
        setAccount(accountRes.data.account);
        setContacts(accountRes.data.contacts || []);
        setDeals(accountRes.data.deals || []);
        setLeads(accountRes.data.leads || []);
        setTasks(accountRes.data.tasks || []);
        setActivities(accountRes.data.activities || []);
        setFormData(accountRes.data.account);
      } else setError('Failed to load account.');
      setInvoices(invoicesRes.data.invoices || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Access denied.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewInvoice = async (invoice) => {
    setPdfActionId(invoice._id + ':preview');
    await previewInvoicePdf(invoice);
    setPdfActionId(null);
  };

  const handleDownloadInvoice = async (invoice) => {
    setPdfActionId(invoice._id + ':download');
    await downloadInvoicePdf(invoice);
    setPdfActionId(null);
  };

  const handleSave = async () => {
    try {
      setSaveLoading(true);
      await api.put(`/crm/accounts/${accountId}`, formData);
      setAccount(formData);
      setIsEditing(false);
      alertService.success('Success', 'Account saved');
    } catch (err) {
      alertService.error('Error', 'Failed to update record');
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return (
      <PageLoader />
    );
  }

  if (!account) {
    return (
      <div className="p-12 text-center space-y-4">
        <div className="w-16 h-16 bg-[#F4F6F9] rounded-full flex items-center justify-center mx-auto text-[#706E6B]">
          <AlertCircle size={32} />
        </div>
        <h2 className="text-xl font-bold text-[#080707]">Record Not Found</h2>
        <button onClick={() => navigate('/dashboard/crm/accounts')} className="text-[#0176D3] font-bold text-sm hover:underline flex items-center justify-center gap-2 mx-auto">
          <ArrowLeft size={16} /> Return to Accounts
        </button>
      </div>
    );
  }

  const totalValue = deals.reduce((sum, d) => sum + convertToBase(d.value || 0, d.currency, ratesCtx), 0);

  return (
    <div className="py-4 sm:py-6 px-0 sm:px-4 space-y-6 max-w-[100rem] mx-auto animate-in fade-in slide-in-from-bottom-2 duration-500">
      
      {/* Navigation */}
      <Breadcrumb className="mb-2" items={[{ label: 'Accounts', to: '/dashboard/crm/accounts' }, { label: account.name }]} />
      <RecordAgentPanel entityType="account" entityId={accountId} />

      {/* Account Header */}
      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 shrink-0 rounded-lg bg-[#0176D3] flex items-center justify-center text-white font-bold ">
              <Building2 size={20} />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <h1 className="text-lg sm:text-xl font-bold text-[#2E2E2E]">{isEditing ? 'Editing Organization' : account.name}</h1>
                <span className="sf-badge sf-badge-neutral">ACCOUNT</span>
                {account.isCustomer && <span className="sf-badge sf-badge-success">CUSTOMER</span>}
                {!healthLoading && health && (
                  <span
                    title={`Unpaid invoices: ${health.unpaidCount} · Open deals: ${health.openDealCount}${health.daysSinceLastActivity !== null ? ` · Last activity ${health.daysSinceLastActivity}d ago` : ''}`}
                    className={cn(
                      "sf-badge",
                      health.score >= 70 ? "sf-badge-success" : health.score >= 40 ? "sf-badge-warning" : "sf-badge-error"
                    )}
                  >
                    HEALTH {health.score}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 text-[12px] text-[#706E6B] font-medium">
                <span className="flex items-center gap-1.5"><Layers size={12} /> {account.industry || 'Market Category'}</span>
                <span className="flex items-center gap-1.5"><Globe size={12} /> {account.website || 'Global Website'}</span>
                {account.parentAccountId?.name && (
                  <span className="flex items-center gap-1.5">
                    <Link size={12} /> Part of:{' '}
                    <span
                      className="text-[#0176D3] font-bold cursor-pointer hover:underline"
                      onClick={() => navigate(`/dashboard/crm/accounts/${account.parentAccountId._id}`)}
                    >
                      {account.parentAccountId.name}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <button onClick={() => setIsEditing(false)} className="h-9 px-4 bg-white border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F3F3] whitespace-nowrap shrink-0">Cancel</button>
                <button onClick={handleSave} disabled={saveLoading} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] whitespace-nowrap shrink-0">Sync Organization</button>
              </>
            ) : (
              <>
                <button onClick={() => setIsEditing(true)} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2 whitespace-nowrap shrink-0">
                  <Edit2 size={14} /> Edit
                </button>
                <button onClick={() => navigate('/dashboard/crm/deals')} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] flex items-center gap-2 whitespace-nowrap shrink-0">
                  <Plus size={14} /> Add Opportunity
                </button>
                <button onClick={() => setShowShareModal(true)} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2 whitespace-nowrap shrink-0">
                  <Share2 size={14} /> Share
                </button>
                <button onClick={() => setShowTransferModal(true)} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2 whitespace-nowrap shrink-0">
                  <UserCog size={14} /> Transfer
                </button>
              </>
            )}
          </div>
        </div>

        {/* Global Summary Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-6 border-t border-[#D8DDE6]">
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Pipeline Value</p>
            <p className="text-[13px] font-bold text-[#2E844A]">{formatCurrency(totalValue || 0, baseCurrency)}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Contacts</p>
            <p className="text-[13px] font-bold text-[#080707]">{contacts.length} SECURED</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Account Site</p>
            <p className="text-[13px] font-bold text-[#0176D3] underline underline-offset-4 decoration-[#D8DDE6]">{account.website || 'N/A'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Location (HQ)</p>
            <p className="text-[13px] font-bold text-[#080707]">{account.city || 'Global'}</p>
          </div>
        </div>

        {/* Ownership & Audit */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-[#D8DDE6]">
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Record Owner</p>
            <p className="text-[13px] font-medium text-[#080707]">{account.ownerName || 'Unassigned'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Created By</p>
            <p className="text-[13px] font-medium text-[#080707]">{account.createdByName || '—'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Last Modified By</p>
            <p className="text-[13px] font-medium text-[#080707]">{account.updatedByName || '—'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Modified On</p>
            <p className="text-[13px] font-medium text-[#706E6B]">{account.updatedAt ? formatDate(account.updatedAt) : '—'}</p>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <Tabs tabs={TABS.map((t) => ({ ...t, key: t.id }))} value={activeTab} onChange={setActiveTab} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          {activeTab === 'overview' && (
            <div className="sf-card p-8 bg-white space-y-8 animate-in fade-in">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Organization Name</label>
                  {isEditing ? (
                    <input className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px]" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                  ) : (
                    <p className="text-[14px] font-bold text-[#080707] border-b border-dashed border-[#D8DDE6] pb-1">{account.name}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Market Industry</label>
                  {isEditing ? (
                    <input className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px]" value={formData.industry} onChange={e => setFormData({...formData, industry: e.target.value})} />
                  ) : (
                    <p className="text-[14px] font-bold text-[#080707] border-b border-dashed border-[#D8DDE6] pb-1 uppercase">{account.industry || 'Management'}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">HQ Location</label>
                  <p className="text-[14px] font-bold text-[#080707] border-b border-dashed border-[#D8DDE6] pb-1 uppercase">{account.city || 'GLOBAL'}</p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Website Hub</label>
                  <a href={account.website} target="_blank" rel="noreferrer" className="text-[14px] font-bold text-[#0176D3] border-b border-dashed border-[#D8DDE6] pb-1 uppercase flex items-center gap-2">
                    {account.website || 'N/A'} <ExternalLink size={12} />
                  </a>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Visibility</label>
                  {isEditing ? (
                    <select className="w-full h-9 px-3 bg-white border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]" value={formData.visibility || 'team'} onChange={e => setFormData({...formData, visibility: e.target.value})}>
                      <option value="team">Team — everyone can see this</option>
                      <option value="private">Private — only owner and shared users</option>
                    </select>
                  ) : (
                    <p className="text-[14px] font-bold text-[#080707] border-b border-dashed border-[#D8DDE6] pb-1">{account.visibility === 'private' ? 'Private' : 'Team'}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[11px] font-semibold text-[#706E6B]">Business Mission & Description</label>
                <div className="p-4 bg-[#F4F6F9] rounded border border-[#D8DDE6] text-[13px] text-[#080707] min-h-[150px] shadow-inner font-medium leading-relaxed">
                  {account.description || 'No strategic business overview recorded for this organization.'}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'contacts' && (
            <div className="space-y-4 animate-in slide-in-from-bottom-4">
              {contacts.length > 0 ? (
                <div className="sf-card overflow-hidden">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-[#F4F6F9] border-b border-[#D8DDE6]">
                        <th className="px-4 py-2.5 text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Name</th>
                        <th className="px-4 py-2.5 text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Designation</th>
                        <th className="px-4 py-2.5 text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#D8DDE6]">
                      {contacts.map(c => (
                        <tr key={c._id} className="hover:bg-[#F7F9FB] transition-colors group">
                          <td className="px-4 py-2.5">
                            <span className="text-[13px] font-bold text-[#0176D3] cursor-pointer hover:underline" onClick={() => navigate(`/dashboard/crm/contacts/${c._id}`)}>{c.name}</span>
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="text-[12px] font-medium text-[#706E6B]">{c.designation || 'Staff'}</span>
                          </td>
                          <td className="px-4 py-2.5">
                             <button onClick={() => navigate(`/dashboard/crm/contacts/${c._id}`)} className="text-[#706E6B] hover:text-[#0176D3]"><ArrowUpRight size={16} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-24 text-center border-2 border-dashed border-[#D8DDE6] rounded bg-white opacity-40">
                  <Users size={48} className="mx-auto mb-4 opacity-10" />
                  <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest">Organization Contacts Empty</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'opportunities' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in slide-in-from-bottom-4">
              {deals.length > 0 ? deals.map(d => (
                <div key={d._id} className="sf-card p-6 space-y-4 hover:border-[#0176D3] transition-all group shadow-sm bg-white border-l-4 border-l-[#2E844A]">
                  <div className="flex justify-between items-start">
                    <h4 className="text-[15px] font-bold text-[#080707] group-hover:text-[#0176D3] transition-colors">{d.title}</h4>
                    <span className="sf-badge sf-badge-success">{formatCurrency(d.value || 0, d.currency || baseCurrency)}</span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider">Probability Index</p>
                    <div className="h-1.5 w-full bg-[#F4F6F9] rounded-full overflow-hidden">
                       <div className="h-full bg-[#2E844A]" style={{ width: `${d.probability || 50}%` }} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[11px] font-bold">
                    <span className="text-[#706E6B] uppercase tracking-widest">Status: <span className="text-[#080707]">{d.status}</span></span>
                    <span className="text-[#0176D3] cursor-pointer" onClick={() => navigate(`/dashboard/crm/deals`)}>View Pipeline</span>
                  </div>
                </div>
              )) : (
                <div className="col-span-full py-24 text-center border-2 border-dashed border-[#D8DDE6] rounded bg-white opacity-40">
                  <TrendingUp size={48} className="mx-auto mb-4 opacity-10" />
                  <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest">No Active Revenue Potential</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'tasks' && (
            <div className="space-y-3 animate-in slide-in-from-bottom-4">
              {tasks.length > 0 ? tasks.map(t => (
                <div
                  key={t._id}
                  onClick={() => navigate(`/dashboard/crm/tasks/${t._id}`)}
                  className="sf-card p-4 flex items-center justify-between hover:border-[#0176D3] transition-all cursor-pointer bg-white"
                >
                  <div>
                    <h4 className="text-[14px] font-bold text-[#080707]">{t.title}</h4>
                    <p className="text-[11px] text-[#706E6B] uppercase tracking-wider mt-1">{t.type}</p>
                  </div>
                  <span className="text-[11px] font-bold text-[#706E6B]">{t.dueDate ? formatDate(t.dueDate) : 'No due date'}</span>
                </div>
              )) : (
                <div className="py-24 text-center border-2 border-dashed border-[#D8DDE6] rounded bg-white opacity-40">
                  <ListChecks size={48} className="mx-auto mb-4 opacity-10" />
                  <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest">No Open Tasks</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'finance' && <CustomerFinancePanel accountId={accountId} name={account?.name} email={account?.email} />}
          {activeTab === 'invoices' && (
            <div className="space-y-4 animate-in slide-in-from-bottom-4">
              {invoices.length > 0 ? (
                <div className="sf-card overflow-hidden">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-[#F4F6F9] border-b border-[#D8DDE6]">
                        <th className="px-4 py-2.5 text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Invoice #</th>
                        <th className="px-4 py-2.5 text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Amount</th>
                        <th className="px-4 py-2.5 text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Status</th>
                        <th className="px-4 py-2.5 text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Due Date</th>
                        <th className="px-4 py-2.5 text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#D8DDE6]">
                      {invoices.map(inv => (
                        <tr key={inv._id} className="hover:bg-[#F7F9FB] transition-colors">
                          <td className="px-4 py-2.5 font-bold text-[#080707]">{inv.invoiceNumber}</td>
                          <td className="px-4 py-2.5 font-bold">{formatCurrency(inv.totalAmount, inv.currency)}</td>
                          <td className="px-4 py-2.5"><span className="sf-badge sf-badge-neutral">{inv.status.toUpperCase()}</span></td>
                          <td className="px-4 py-2.5 text-[12px] text-[#706E6B]">{new Date(inv.dueDate).toLocaleDateString()}</td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => handlePreviewInvoice(inv)}
                                disabled={pdfActionId === inv._id + ':preview'}
                                className="text-[#0176D3] hover:underline flex items-center gap-1 text-[12px] font-bold disabled:opacity-50"
                              >
                                {pdfActionId === inv._id + ':preview' ? <Loader size={12} className="animate-spin" /> : <Eye size={12} />} Preview
                              </button>
                              <button
                                onClick={() => handleDownloadInvoice(inv)}
                                disabled={pdfActionId === inv._id + ':download'}
                                className="text-[#0176D3] hover:underline flex items-center gap-1 text-[12px] font-bold disabled:opacity-50"
                              >
                                {pdfActionId === inv._id + ':download' ? <Loader size={12} className="animate-spin" /> : <Download size={12} />} Download
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-24 text-center border-2 border-dashed border-[#D8DDE6] rounded bg-white opacity-40">
                  <Receipt size={48} className="mx-auto mb-4 opacity-10" />
                  <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest">No Invoices Yet</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'files' && (
            <div className="sf-card p-4 animate-in slide-in-from-bottom-4">
              <RelatedFiles module="accounts" moduleId={accountId} recordName={account?.name} />
            </div>
          )}

          {activeTab === 'formActivity' && (
            <div className="sf-card p-4 animate-in slide-in-from-bottom-4">
              <FormActivityPanel recordType="account" recordId={accountId} />
            </div>
          )}

          {activeTab === 'history' && (
            <UnifiedTimeline activities={activities} tasks={tasks} />
          )}

          {activeTab === 'hierarchy' && (
            <div className="space-y-4 animate-in slide-in-from-bottom-4">
              <div className="flex items-center gap-1 p-1 bg-[#F3F2F2] border border-[#D8DDE6] rounded-md w-fit">
                {[{ id: 'relationships', label: 'Relationships' }, { id: 'orgChart', label: 'Org Chart' }].map(m => (
                  <button
                    key={m.id}
                    className={cn(
                      "px-4 py-1.5 rounded text-[11px] font-bold uppercase tracking-wider transition-all",
                      hierarchyMode === m.id ? "bg-white text-[#0176D3] shadow-sm border border-[#D8DDE6]" : "text-[#706E6B] hover:text-[#0176D3]"
                    )}
                    onClick={() => setHierarchyMode(m.id)}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <AccountHierarchyTree mode={hierarchyMode} account={account} contacts={contacts} leads={leads} deals={deals} />
            </div>
          )}
        </div>

        {/* Intelligence Sidebar */}
        <div className="lg:col-span-4 space-y-6">
          <div className="sf-card p-6 bg-white space-y-6">
            <h3 className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest border-b border-[#D8DDE6] pb-3 mb-4">Account Analytics</h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-4 bg-[#F4F6F9] rounded text-center">
                 <p className="text-[10px] font-bold text-[#706E6B] uppercase mb-1">Total Leads</p>
                 <p className="text-xl font-bold text-[#080707]">{leads.length}</p>
              </div>
              <div className="p-4 bg-[#F4F6F9] rounded text-center">
                 <p className="text-[10px] font-bold text-[#706E6B] uppercase mb-1">Open Deals</p>
                 <p className="text-xl font-bold text-[#2E844A]">{deals.filter(d => d.status === 'open').length}</p>
              </div>
            </div>

            {leads.length > 0 && (
              <div className="pt-4 border-t border-[#D8DDE6] space-y-2">
                <h4 className="text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Related Leads</h4>
                {leads.slice(0, 5).map(l => (
                  <div key={l._id} className="flex items-center justify-between py-1.5">
                    <span
                      className="text-[13px] font-bold text-[#0176D3] cursor-pointer hover:underline truncate"
                      onClick={() => navigate(`/dashboard/crm/leads/${l._id}`)}
                    >
                      {l.name}
                    </span>
                    <span className="sf-badge sf-badge-neutral shrink-0">{l.status}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-4 pt-4">
              <div className="flex items-center justify-between py-2 border-b border-[#D8DDE6]">
                <span className="text-[12px] font-medium text-[#706E6B]">Last Contact</span>
                <span className="text-[11px] font-bold text-[#080707]">
                  {health?.daysSinceLastActivity != null ? `${health.daysSinceLastActivity} DAYS AGO` : 'NO ACTIVITY YET'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[#D8DDE6]">
                <span className="text-[12px] font-medium text-[#706E6B]">Risk Level</span>
                <span className={cn(
                  'sf-badge',
                  health == null ? 'sf-badge-neutral' : health.score >= 70 ? 'sf-badge-success' : health.score >= 40 ? 'sf-badge-warning' : 'sf-badge-error'
                )}>
                  {health == null ? 'UNKNOWN' : health.score >= 70 ? 'LOW' : health.score >= 40 ? 'MEDIUM' : 'HIGH'}
                </span>
              </div>
            </div>
          </div>

          {subsidiaries.length > 0 && (
            <div className="sf-card p-6 bg-white space-y-4">
              <h3 className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest border-b border-[#D8DDE6] pb-3">
                Subsidiaries ({subsidiaries.length})
              </h3>
              <div className="space-y-2">
                {subsidiaries.map(s => (
                  <div key={s._id} className="flex items-center justify-between py-1.5">
                    <span
                      className="text-[13px] font-bold text-[#0176D3] cursor-pointer hover:underline"
                      onClick={() => navigate(`/dashboard/crm/accounts/${s._id}`)}
                    >
                      {s.name}
                    </span>
                    <ExternalLink size={12} className="text-[#706E6B]" />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="sf-card p-6 bg-[#0176D3] text-white space-y-3 relative overflow-hidden">
            <div className="absolute -right-4 -bottom-4 opacity-10">
              <ShieldCheck size={100} />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">Account Health</p>
            <p className="text-[13px] font-bold leading-relaxed relative z-10">Verified organizational node. Multi-tenant isolation secured at hardware layer.</p>
          </div>
        </div>
      </div>
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        resourceType="account"
        resourceId={account._id}
        resourceLabel="Account"
      />
      <TransferOwnershipModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        resourceType="account"
        resourceId={account._id}
        currentOwnerId={account.ownerId}
        onTransferred={() => fetchAccountData()}
      />
    </div>
  );
}
